import type { EngineInput } from '@conciair/scheduler-core';
import { supabase } from '../data/supabase';

/**
 * Canonical JSON: object keys sorted recursively, no whitespace. Two
 * snapshots of identical data hash identically regardless of how Postgres
 * ordered the keys (spec 3.2: input_hash = SHA-256 of canonical JSON).
 */
export function canonicalJson(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return '[' + value.map(canonicalJson).join(',') + ']';
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  return '{' + keys.map((k) => JSON.stringify(k) + ':' + canonicalJson(obj[k])).join(',') + '}';
}

export async function sha256Hex(text: string): Promise<string> {
  const bytes = new TextEncoder().encode(text);
  const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

export async function hashSnapshot(input: EngineInput): Promise<string> {
  return sha256Hex(canonicalJson(input));
}

/** Spec 6.1 step 1: the V1 readInput() object straight from Postgres. */
export async function fetchSnapshot(orgId: string): Promise<{ input: EngineInput; hash: string }> {
  const { data, error } = await supabase.rpc('engine_input_snapshot', { p_org: orgId });
  if (error) throw new Error(error.message);
  const input = data as unknown as EngineInput;
  return { input, hash: await hashSnapshot(input) };
}

/**
 * Resolve a V1 validation location ("Overrides row 3") to the record's
 * database id via the snapshot's _ref, so messages can link to records.
 */
const WHERE_RE = /^(AircraftTypes|Aircraft|Pilots|Qualifications|Unavailability|Overrides|Transitions|AircraftPairing|AlternatingPilots|PreviousRotation|Config)(?: row (\d+))?$/;

const COLLECTION: Record<string, keyof EngineInput> = {
  AircraftTypes: 'aircraftTypes',
  Aircraft: 'aircraft',
  Pilots: 'pilots',
  Qualifications: 'qualifications',
  Unavailability: 'unavailability',
  Overrides: 'overrides',
  Transitions: 'transitions',
  AircraftPairing: 'aircraftPairings',
  AlternatingPilots: 'alternating',
  PreviousRotation: 'previousRotations',
};

export interface ResolvedWhere {
  area: string;
  row: number | null;
  ref: string | null;
}

export function resolveWhere(where: string, input: EngineInput): ResolvedWhere {
  const m = WHERE_RE.exec(where.trim());
  if (!m) return { area: where, row: null, ref: null };
  const area = m[1] ?? where;
  const row = m[2] ? Number(m[2]) : null;
  let ref: string | null = null;
  const key = COLLECTION[area];
  if (row !== null && key) {
    const list = input[key] as Array<{ _ref?: string }> | undefined;
    ref = list?.[row - 1]?._ref ?? null;
  }
  return { area, row, ref };
}
